/**
 * Lightweight utility for cleaning article names.
 * Extracted from recipeParser to avoid loading the full 1700+ line recipe parser
 * on initial page load and in core app stores.
 */

const COMMON_UNITS = [
  'g', 'gramm', 'kg', 'kilogramm', 'mg', 'milligramm',
  'ml', 'milliliter', 'l', 'liter', 'cl', 'zentiliter', 'dl', 'deziliter',
  'el', 'esslöffel', 'essloeffel', 'tl', 'teelöffel', 'teeloeffel',
  'tsp', 'tbsp',
  'stück', 'stk', 'stk.', 'stueck', 'st', 'st.',
  'dose', 'dosen', 'flasche', 'flaschen', 'glas', 'gläser', 'glaeser',
  'becher', 'tasse', 'tassen', 'cup', 'cups',
  'packung', 'packungen', 'pck', 'pck.', 'pkg', 'pkg.', 'pkt', 'pkt.', 'päckchen', 'paeckchen',
  'beutel', 'tüte', 'tuete', 'tüten', 'tueten',
  'prise', 'prisen', 'messerspitze', 'msp', 'msp.',
  'bund', 'bündel', 'buendel', 'strauß', 'strauss',
  'zehe', 'zehen', 'knolle', 'knollen',
  'stange', 'stangen', 'zweig', 'zweige', 'blatt', 'blätter', 'blaetter',
  'scheibe', 'scheiben', 'spritzer', 'schuss', 'tropfen',
  'handvoll', 'portion', 'portionen'
]

const COMMON_UNITS_SET = new Set(COMMON_UNITS)

/**
 * Strips secondary parenthetical quantity/unit annotations like "(5EL)", "(ca. 5 EL)", "(1 Dose)" from ingredient names.
 */
export function stripMeasurementParentheses(name: string): string {
  return name.replace(/\s*\(\s*(?:ca\.?\s*|etwa\s*)?([0-9]+(?:[,./][0-9]+)?)\s*([a-zA-ZäöüÄÖÜ.]+)\s*\)/gi, (match, _qty, rawUnit) => {
    const unitLower = rawUnit.toLowerCase().replace(/[.,]$/, '')
    if (COMMON_UNITS_SET.has(unitLower)) {
      return ''
    }
    return match
  }).trim()
}

/**
 * Strips parentheses, allergen tags, packaging/preparation notes from an article name.
 * Prevents importing nonsensical parenthetical article names like "(Fisch)" or "Lachs (Fisch)".
 * e.g.:
 * "(Fisch)" -> "Fisch"
 * "Lachs (Fisch)" -> "Lachs"
 * "Tomaten (Dose)" -> "Tomaten"
 * "Erbsen (TK)" -> "Erbsen"
 * "Petersilie (gehackt)" -> "Petersilie"
 * "Weizenmehl (Type 405)" -> "Weizenmehl"
 */
export function cleanArticleName(rawName: string): string {
  if (!rawName || typeof rawName !== 'string') return ''
  let clean = rawName.trim()

  // 1. Strip surrounding quotes or parentheses if the whole name is wrapped: "(Fisch)" -> "Fisch", "[Lachs]" -> "Lachs"
  while (/^[(["'„“](.*)[)\]"'“”]$/.test(clean)) {
    const inside = clean.slice(1, -1).trim()
    if (!inside) break
    clean = inside
  }

  // 2. Strip measurement parentheses like (500g), (1 Dose), (2 EL)
  clean = stripMeasurementParentheses(clean)

  // 3. Strip leading parenthetical qualifiers e.g. "(TK) Erbsen" -> "Erbsen"
  clean = clean.replace(/^\(\s*(?:tk|tiefgek[üu]hlt|tiefk[üu]hl|frisch|dose|glas|bio)\s*\)\s*/i, '').trim()

  // 4. Strip trailing parenthetical allergen tags e.g. (Fisch), (Gluten), (Milch), (Soja), etc.
  clean = clean.replace(/\s*\(\s*(?:fisch|fish|gluten|laktose|lactose|milch|milk|eier?|eggs?|soja(?:bohnen)?|soy(?:beans?)?|sellerie|celery|senf|mustard|sesam(?:samen)?|sesame|n[üu]sse|nuts?|schalenfr[üu]chte|erdn[üu]sse|peanuts?|krebstiere|crustaceans?|weichtiere|molluscs?|lupinen|lupin|sulfite?|sulphites?)\s*\)$/i, '').trim()

  // 5. Strip trailing parenthetical preparation, container, or dietary annotations
  // e.g. (Dose), (TK), (Tiefkühl), (Glas), (Packung), (gewürfelt), (gehackt), (gerieben), (optional), (frisch), (Type 405), (z.B. ...)
  clean = clean.replace(/\s*\(\s*(?:tk|tiefgek[üu]hlt|tiefk[üu]hl|frisch|dose|dosen|glas|gl[äa]ser|beutel|packung|pkg|bund|getrocknet|gew[üu]rfelt|gehackt|gerieben|gemahlen|geschnitten|entkernt|gesch[äa]lt|optional|nach\s+belieben|nach\s+bedarf|nach\s+geschmack|nach\s+wunsch|zum\s+garnieren|zum\s+servieren|zum\s+braten|zum\s+bestreuen|z\.?b\.?[^)]*|type\s+[0-9]+|oder\s+[^)]+)\s*\)$/i, '').trim()

  // 6. If stripping inner parts revealed outer brackets again, strip them:
  while (/^[([](.*)[)\]]$/.test(clean)) {
    const inside = clean.slice(1, -1).trim()
    if (!inside) break
    clean = inside
  }

  // 7. Strip any remaining trailing parenthetical clause if preceded by a valid name
  if (clean.includes('(') && clean.endsWith(')')) {
    const withoutTrailing = clean.replace(/\s*\([^)]*\)$/, '').trim()
    if (withoutTrailing.length >= 2) {
      clean = withoutTrailing
    }
  }

  // 8. Strip any loose/stray brackets at edges
  clean = clean.replace(/^[()[\]{}]+|[()[\]{}]+$/g, '').trim()

  return clean
}
